/** Planned session: display card + editor dialog (edit, skip, delete, add). */
import { Term, TermText } from "./Term";
import { useState, type FormEvent } from "react";
import { download } from "@/api/client";
import { useCreateSession, useDeleteSession, useMe, useStatus, useUpdateSession } from "@/api/hooks";
import { SESSION_KINDS, type PlannedSession, type SessionKind } from "@/api/schemas";
import { athleteToday, dayLong, dayYear, hours, kindLabel, meters, num, sportLabel } from "@/lib/format";
import { Badge, Dialog } from "./ui";
import { KomootPicker } from "./Komoot";
import { useToast } from "./Toast";
import { SKIP_LABEL, SkipDialog, WeatherChip } from "./Coach";

const STATUS_TONE = { planned: "", done: "good", skipped: "warn", modified: "info" } as const;

const ROUTE_KINDS = new Set(["endurance", "recovery", "tempo_climbs", "long", "back_to_back"]);
const OPEN = new Set(["planned", "modified"]);

export function SessionCard({
  s,
  onEdit,
  compact = false,
}: {
  s: PlannedSession;
  onEdit?: (s: PlannedSession) => void;
  compact?: boolean;
}) {
  const toast = useToast();
  const today = athleteToday(useStatus().data?.today);
  const indoor = s.sport === "indoor_cycling";
  return (
    <article className={`session ${s.kind} ${s.status}`} data-testid="session">
      <div className="row between">
        <div className="title">
          {kindLabel(s.kind)} · {hours(s.target_hours)}
          {indoor && (
            <>
              {" "}
              · <Badge tone="info">Indoor</Badge>
            </>
          )}
        </div>
        <div className="row" style={{ gap: 6 }}>
          {s.status !== "planned" && (
            <Badge tone={STATUS_TONE[s.status]}>
              {s.status}
              {s.skip_reason ? ` · ${SKIP_LABEL[s.skip_reason]}` : ""}
            </Badge>
          )}
          {s.source !== "plan" && <Badge>{s.source === "user" ? "yours" : "adapted"}</Badge>}
          {onEdit && (
            <button
              className="ghost small"
              onClick={() => onEdit(s)}
              aria-label={`Edit ${kindLabel(s.kind)} on ${s.day}`}
            >
              Edit
            </button>
          )}
        </div>
      </div>
      {!compact && (
        <>
          <div className="kpi-row" style={{ marginTop: 4 }}>
            <span>{sportLabel(s.sport)}</span>
            {s.hr_cap != null && (
              <span>
                <Term k="hrCap">HR cap</Term> <b>{num(s.hr_cap)}</b>
              </span>
            )}
            {s.power_low_w != null && s.power_high_w != null && (
              <span>
                Power{" "}
                <b>
                  {num(s.power_low_w)}–{num(s.power_high_w)} W
                </b>
              </span>
            )}
            {s.target_ascent_m != null && (
              <span>
                Climb <b>{meters(s.target_ascent_m)}</b>
              </span>
            )}
            {s.min_unpaved_pct != null && (
              <span>
                Unpaved ≥ <b>{num(s.min_unpaved_pct * 100)}%</b>
              </span>
            )}
            <span>
              <Term k="load">Load</Term> <b>{num(s.target_load)}</b>
            </span>
          </div>
          <Description text={s.description} />
          {s.skip_note && <p className="muted">“{s.skip_note}”</p>}
          {s.status !== "skipped" && <WeatherChip w={s.weather} warnings={s.weather_warnings} />}
          <div className="row" style={{ marginTop: 8, gap: 8 }}>
            {indoor && s.kind !== "rest" && (
              <button
                className="small"
                onClick={() =>
                  download(`/plan/sessions/${s.id}/workout.zwo`, `workout-${s.day}.zwo`).catch((e: unknown) =>
                    toast.error(e),
                  )
                }
              >
                Download workout (.zwo)
              </button>
            )}
            {ROUTE_KINDS.has(s.kind) &&
              s.sport !== "indoor_cycling" &&
              OPEN.has(s.status) &&
              s.day >= today && <KomootPicker day={s.day} />}
          </div>
        </>
      )}
    </article>
  );
}

type Draft = {
  day: string;
  kind: SessionKind;
  sport: string;
  target_hours: number;
  hr_cap: string;
  description: string;
  note: string;
};

/** The coach's guidance, then the athlete's own note (if any) on its own line. */
export function Description({ text, className }: { text: string; className?: string }) {
  if (!text) return null;
  const { guidance, note } = splitNote(text);
  return (
    <>
      {guidance && (
        <p className={className}>
          <TermText text={guidance} />
        </p>
      )}
      {note && (
        <p className={`muted ${className ?? ""}`} data-testid="session-note">
          Your note: {note}
        </p>
      )}
    </>
  );
}

/** A plan session's text is the coach's guidance; the athlete's own note is kept after this marker, so
 * editing the note never wipes the guidance (one `description` field, no schema change). */
export const NOTE_MARK = "\n\nYour note: ";

export function splitNote(description: string): { guidance: string; note: string } {
  const i = description.indexOf(NOTE_MARK);
  return i < 0
    ? { guidance: description, note: "" }
    : { guidance: description.slice(0, i), note: description.slice(i + NOTE_MARK.length) };
}

export function joinNote(guidance: string, note: string): string {
  return note.trim() ? `${guidance}${NOTE_MARK}${note.trim()}` : guidance;
}

const SPORTS = ["gravel", "cycling", "mtb", "indoor_cycling", "hockey", "running", "hiking", "strength"];

/** Edit an existing session (`session`) or create a new one on `newDay`. */
export function SessionEditor({
  session,
  newDay,
  onClose,
  onDescribeRoute,
}: {
  session: PlannedSession | null;
  newDay?: string | null;
  onClose: () => void;
  onDescribeRoute?: (day: string) => void;
}) {
  const [skipping, setSkipping] = useState<PlannedSession | null>(null);
  const open = !!session || !!newDay;
  const toast = useToast();
  const update = useUpdateSession();
  const create = useCreateSession();
  const del = useDeleteSession();
  const me = useMe();
  const [draft, setDraft] = useState<Draft | null>(null);
  // the coach's text stays read-only for the plan's own sessions; the athlete writes a note of their own
  const coachText = !!session && session.source === "plan";
  const split = session ? splitNote(session.description) : { guidance: "", note: "" };
  const cur: Draft =
    draft ??
    (session
      ? {
          day: session.day,
          kind: session.kind,
          sport: session.sport,
          target_hours: session.target_hours,
          hr_cap: session.hr_cap?.toString() ?? "",
          description: coachText ? split.guidance : session.description,
          note: coachText ? split.note : "",
        }
      : {
          day: newDay ?? "",
          kind: "endurance",
          sport: "gravel",
          target_hours: 1.5,
          hr_cap: me.data?.zones?.lt1 != null ? String(me.data.zones.lt1) : "",
          description: "",
          note: "",
        });
  const description = coachText ? joinNote(cur.description, cur.note) : cur.description;
  const set = (p: Partial<Draft>) => setDraft({ ...cur, ...p });
  const close = () => {
    setDraft(null);
    onClose();
  };
  const busy = update.isPending || create.isPending || del.isPending;

  async function save(e: FormEvent) {
    e.preventDefault();
    const hr = cur.hr_cap.trim() === "" ? null : Number(cur.hr_cap);
    try {
      if (session) {
        await update.mutateAsync({
          id: session.id,
          patch: {
            day: cur.day,
            kind: cur.kind,
            sport: cur.sport,
            target_hours: cur.target_hours,
            hr_cap: hr,
            description,
          },
        });
        toast.ok("Session updated");
      } else {
        await create.mutateAsync({
          day: cur.day,
          kind: cur.kind,
          sport: cur.sport,
          target_hours: cur.target_hours,
          hr_cap: hr,
          description,
        });
        toast.ok("Session added");
      }
      close();
    } catch (err) {
      toast.error(err);
    }
  }
  async function setStatus(status: PlannedSession["status"]) {
    if (!session) return;
    try {
      await update.mutateAsync({ id: session.id, patch: { status } });
      toast.ok("Restored");
      close();
    } catch (err) {
      toast.error(err);
    }
  }
  async function remove() {
    if (!session) return;
    try {
      await del.mutateAsync(session.id);
      toast.ok("Session deleted");
      close();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <>
      <Dialog open={open} onClose={close} title={session ? `Edit · ${dayLong(session.day)}` : "Add session"}>
        <form className="stack" onSubmit={save}>
          <div className="form-grid">
            <label>
              Day
              <input type="date" required value={cur.day} onChange={(e) => set({ day: e.target.value })} />
              {cur.day && (
                <small className="muted" data-testid="day-words">
                  {dayYear(cur.day)}
                </small>
              )}
            </label>
            <label>
              Type
              <select value={cur.kind} onChange={(e) => set({ kind: e.target.value as SessionKind })}>
                {SESSION_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {kindLabel(k)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sport
              <select value={cur.sport} onChange={(e) => set({ sport: e.target.value })}>
                {SPORTS.map((s) => (
                  <option key={s} value={s}>
                    {sportLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Hours
              <input
                type="number"
                min={0}
                max={24}
                step={0.25}
                value={cur.target_hours}
                onChange={(e) => set({ target_hours: Number(e.target.value) })}
              />
            </label>
            <label>
              HR cap
              <input
                type="number"
                min={80}
                max={220}
                value={cur.hr_cap}
                onChange={(e) => set({ hr_cap: e.target.value })}
                placeholder="none"
              />
            </label>
          </div>
          {coachText ? (
            <>
              {cur.description && (
                <div className="stack" style={{ gap: 4 }} data-testid="coach-guidance">
                  <small className="muted">The coach's guidance</small>
                  <p style={{ margin: 0 }}>{cur.description}</p>
                </div>
              )}
              <label>
                Your note
                <textarea
                  value={cur.note}
                  onChange={(e) => set({ note: e.target.value })}
                  placeholder="optional: anything you want to remember for this session"
                  maxLength={500}
                />
              </label>
            </>
          ) : (
            <label>
              Notes
              <textarea value={cur.description} onChange={(e) => set({ description: e.target.value })} />
            </label>
          )}
          <div className="row between">
            <div className="row">
              {session && session.status !== "skipped" && (
                <button
                  type="button"
                  onClick={() => {
                    setSkipping(session);
                    close();
                  }}
                  disabled={busy}
                >
                  Skip…
                </button>
              )}
              {session && session.status === "skipped" && (
                <button type="button" onClick={() => setStatus("planned")} disabled={busy}>
                  Un-skip
                </button>
              )}
              {session && (
                <button type="button" className="danger" onClick={remove} disabled={busy}>
                  Delete
                </button>
              )}
            </div>
            <button className="primary" type="submit" disabled={busy}>
              {session ? "Save" : "Add"}
            </button>
          </div>
          <small className="faint">Your edits are kept when the plan regenerates.</small>
        </form>
      </Dialog>
      <SkipDialog session={skipping} onClose={() => setSkipping(null)} onDescribeRoute={onDescribeRoute} />
    </>
  );
}
